/**
 * Editable table section for class, family, genus, flair, and image tables.
 * Handles rendering, editing, adding, deleting, and moving items.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useAdminContext } from './AdminContext';
import Breadcrumbs from './components/Breadcrumbs';
import type { TabValue } from './types';

interface TableSectionProps {
  tableName: TabValue;
  handleTableEdit: (row: any) => void;
  handleMoveItem: (type: string, id: number, name: string, currentParent?: { parentType: string; parentName: string }) => void;
  handleDeleteItem: (tableName: string, id: number) => void;
  handleAddItem: (tableName: string) => void;
  handleClassClick: (className: string) => void;
  handleFamilyClick: (familyName: string) => void;
  handleGenusClick: (genusName: string) => void;
  newItemData: { [key: string]: any };
  setNewItemData: (data: { [key: string]: any }) => void;
  isAddingItem: boolean;
}

export default function TableSection({
  tableName,
  handleTableEdit,
  handleMoveItem,
  handleDeleteItem,
  handleAddItem,
  handleClassClick,
  handleFamilyClick,
  handleGenusClick,
  newItemData,
  setNewItemData,
  isAddingItem,
}: TableSectionProps) {
  const context = useAdminContext();
  const {
    tableData,
    isLoadingTable,
    subreddit,
    isSubscribed,
    selectedClass,
    selectedFamily,
    selectedGenus,
    selectedFlair,
    setSelectedGenus,
    setSelectedFlair,
    setActiveTab,
    parentOptions,
    loadTableData,
    CDN_BASE_URL,
    subscriptions,
    isDeletingItem,
    setMessage,
    setTableDataCache,
    setTableData,
    user,
  } = context;

  if (!tableName || !['class', 'family', 'genus', 'flair', 'image'].includes(tableName)) {
    return <Alert severity="info">Please select a table to view.</Alert>;
  }

  if (isSubscribed) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        This subreddit is subscribed to another subreddit. Flair management is read-only. Changes will be synced from the parent subreddit nightly.
      </Alert>
    );
  }

  if (isLoadingTable) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
        <CircularProgress />
        <Typography>Loading {tableName} data...</Typography>
      </Box>
    );
  }

  if (tableData.length === 0) {
    return (
      <Box>
        <Breadcrumbs />
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const filters: { class?: string; family?: string; genus?: string } = {};
                if (selectedClass) filters.class = selectedClass;
                if (selectedFamily) filters.family = selectedFamily;
                if (selectedGenus) filters.genus = selectedGenus;
                loadTableData(tableName, filters);
              }}
            >
              Retry
            </Button>
          }
        >
          No {tableName} data found for /r/{subreddit}. Click Retry to reload.
        </Alert>
      </Box>
    );
  }

  const columns = Object.keys(tableData[0]);
  const idColumn = columns.find((col) => col.includes('id') && col !== 'sub_id');

  const getColumnWidth = (col: string) => {
    const longTextColumns = ['name', 'text', 'source', 'code', 'abbreviated'];
    if (longTextColumns.some((l) => col.toLowerCase().includes(l))) {
      return { minWidth: 200 };
    }
    return { minWidth: 100 };
  };


  return (
    <Box>
      <Breadcrumbs />
      {tableName === 'flair' && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Genus</InputLabel>
            <Select
              value={selectedGenus || ''}
              onChange={(e) => {
                const genusName = e.target.value as string;
                if (genusName) {
                  setSelectedGenus(genusName);
                  const filters: { class?: string; family?: string; genus?: string } = {};
                  if (selectedClass) filters.class = selectedClass;
                  if (selectedFamily) filters.family = selectedFamily;
                  filters.genus = genusName;
                  loadTableData('flair', filters);
                } else {
                  setSelectedGenus(null);
                  const filters: { class?: string; family?: string } = {};
                  if (selectedClass) filters.class = selectedClass;
                  if (selectedFamily) filters.family = selectedFamily;
                  loadTableData('flair', filters);
                }
              }}
              label="Filter by Genus"
            >
              <MenuItem value="">
                <em>All Genuses</em>
              </MenuItem>
              {(parentOptions.genus || []).map((genus: any) => (
                <MenuItem key={genus.genus_id} value={genus.genus_name}>
                  {genus.genus_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '70vh',
          overflowX: 'auto',
          overflowY: 'auto',
        }}
      >
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    ...getColumnWidth(col),
                  }}
                >
                  {col.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row: any) => {
              const rowId = row[idColumn || 'id'];
              const nameColumn =
                columns.find((col) => col === `${tableName}_name` || (col.includes('name') && !col.includes('_id') && col !== 'sub_name')) ||
                columns.find((col) => col.includes('name') && !col.includes('_id'));
              const rowName = nameColumn ? String(row[nameColumn] || '') : '';

              const imageSource =
                row['image_source'] ||
                row['image_name'] ||
                row['class_image'] ||
                row['family_image'] ||
                row['genus_image'] ||
                '';
              const imageUrl =
                imageSource && subreddit
                  ? (() => {
                      const cleanSource = String(imageSource).replace('.png', '').trim();
                      const imageName = cleanSource.includes('/') ? cleanSource.split('/').pop() : cleanSource;
                      const cdnSub = isSubscribed && subscriptions.length > 0 ? subscriptions[0] : subreddit;
                      return `${CDN_BASE_URL}/60x60/${cdnSub}/${imageName}.png`;
                    })()
                  : '';

              const isClickable =
                (tableName === 'class' && !selectedClass) ||
                (tableName === 'family' && !selectedFamily && selectedClass) ||
                (tableName === 'genus' && !selectedGenus && selectedFamily) ||
                tableName === 'flair';

              const handleRowClick = () => {
                if (!isClickable || !rowName) return;
                if (tableName === 'class') {
                  handleClassClick(rowName);
                } else if (tableName === 'family') {
                  handleFamilyClick(rowName);
                } else if (tableName === 'genus') {
                  handleGenusClick(rowName);
                } else if (tableName === 'flair') {
                  setSelectedFlair(rowName);
                  if (row['genus_name']) {
                    setSelectedGenus(row['genus_name']);
                  }
                  setActiveTab('image');
                }
              };

              return (
                <TableRow
                  key={rowId}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                    cursor: isSubscribed ? 'default' : isClickable ? 'pointer' : 'default',
                  }}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]') || (e.target as HTMLElement).closest('button')) {
                      return;
                    }
                    if (!isSubscribed && isClickable) {
                      handleRowClick();
                    }
                  }}
                >
                  {columns.map((col) => {
                    const rawValue = row[col];
                    const cellValue = String(rawValue || '');

                    const editableBooleanFields = tableName === 'family'
                      ? ['selectable_flair', 'selectable_inline']
                      : tableName === 'image'
                        ? ['default_flair', 'active_image']
                        : [];
                    const readOnlyBooleanFields = ['active_flair', 'active_image', 'default_image', 'default_flair'].filter(
                      (f) => !editableBooleanFields.includes(f)
                    );
                    // Image table: default_flair and active_image are always editable (explicit check in case of column name quirks)
                    const isEditableBoolean =
                      (tableName === 'image' && (col === 'default_flair' || col === 'active_image')) ||
                      editableBooleanFields.includes(col);
                    const isReadOnlyBoolean = !isEditableBoolean && readOnlyBooleanFields.includes(col);
                    const isBooleanField = isEditableBoolean || isReadOnlyBoolean;
                    const boolValue = isBooleanField ? rawValue === 1 || rawValue === true || rawValue === '1' : null;

                    const isImageColumn =
                      (tableName === 'image' && (col === 'image_name' || col === 'image_source')) ||
                      col === 'class_image' ||
                      col === 'family_image' ||
                      col === 'genus_image';

                    return (
                      <TableCell
                        key={col}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: getColumnWidth(col).minWidth,
                          py: 0.5,
                          ...getColumnWidth(col),
                        }}
                        title={isBooleanField ? (boolValue ? 'Yes' : 'No') : cellValue}
                        onClick={(e) => {
                          if (isEditableBoolean) {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {isEditableBoolean ? (
                          <Switch
                            checked={boolValue || false}
                            size="small"
                            disabled={isSubscribed}
                            onChange={async (e) => {
                              e.stopPropagation();
                              if (!subreddit || !user?.username) return;
                              const newValue = e.target.checked ? 1 : 0;
                              const currentSubreddit = subreddit;
                              const currentUsername = user.username;
                              try {
                                const { API_BASE_URL } = await import('../../config');
                                const response = await fetch(
                                  `${API_BASE_URL}/admin/tables/${tableName}?subreddit=${encodeURIComponent(currentSubreddit)}`,
                                  {
                                    method: 'PUT',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${currentUsername}`,
                                    },
                                    body: JSON.stringify({
                                      subreddit: currentSubreddit,
                                      row: { ...row, [col]: newValue },
                                    }),
                                  }
                                );
                                if (response.ok) {
                                  const updatedData = tableData.map((r: any) => (r[idColumn || 'id'] === rowId ? { ...r, [col]: newValue } : r));
                                  setTableData(updatedData);
                                  const cacheKey = `${subreddit}_${tableName}_${selectedClass || ''}_${selectedFamily || ''}_${selectedGenus || ''}_${selectedFlair || ''}`;
                                  setTableDataCache((prev: { [key: string]: any[] }) => {
                                    const newCache = { ...prev };
                                    delete newCache[cacheKey];
                                    return newCache;
                                  });
                                } else {
                                  const errorData = await response.json();
                                  setMessage({ type: 'error', text: errorData.error || 'Failed to update' });
                                }
                              } catch (error) {
                                setMessage({ type: 'error', text: 'Failed to update toggle' });
                              }
                            }}
                          />
                        ) : isReadOnlyBoolean ? (
                          <Switch checked={boolValue || false} size="small" disabled sx={{ pointerEvents: 'none' }} />
                        ) : isImageColumn && imageUrl ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img
                              src={imageUrl}
                              alt={cellValue}
                              loading="lazy"
                              decoding="async"
                              style={{ width: 20, height: 20, objectFit: 'contain' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <span>{cellValue}</span>
                          </Box>
                        ) : (
                          cellValue
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!isSubscribed && (
                        <>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => handleTableEdit(row)}>
                            Edit
                          </Button>
                          {(tableName === 'flair' || tableName === 'genus' || tableName === 'family') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const nameColumn = `${tableName}_name`;
                                const itemName = row[nameColumn] ? String(row[nameColumn]) : '';
                                let currentParent: { parentType: string; parentName: string } | undefined;
                                if (tableName === 'flair' && row['genus_name']) {
                                  currentParent = { parentType: 'genus', parentName: String(row['genus_name']) };
                                } else if (tableName === 'genus' && row['family_name']) {
                                  currentParent = { parentType: 'family', parentName: String(row['family_name']) };
                                } else if (tableName === 'family' && row['class_name']) {
                                  currentParent = { parentType: 'class', parentName: String(row['class_name']) };
                                }
                                handleMoveItem(tableName, rowId, itemName, currentParent);
                              }}
                            >
                              Move
                            </Button>
                          )}
                          {!isSubscribed && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteItem(tableName, rowId)}
                              disabled={isDeletingItem === rowId}
                            >
                              {isDeletingItem === rowId ? 'Deleting...' : 'Delete'}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add New Item Form */}
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Add New {tableName.charAt(0).toUpperCase() + tableName.slice(1)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {tableName === 'class' && (
            <>
              <TextField
                label="Class Name"
                value={newItemData.class_name || ''}
                onChange={(e) => setNewItemData({ ...newItemData, class_name: e.target.value })}
                required
              />
              <TextField
                label="Order"
                type="number"
                value={newItemData.class_order ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, class_order: parseInt(e.target.value) || 0 })}
                helperText="0 = alphabetical order, >0 = custom numeric order"
              />
              <FormControl fullWidth>
                <InputLabel id="new-class-flair-size-label" shrink>Flair size</InputLabel>
                <Select
                  labelId="new-class-flair-size-label"
                  value={
                    newItemData.flair_width != null && newItemData.flair_height != null
                      ? (Number(newItemData.flair_height) === 40 ? '60x40' : '60x60')
                      : '60x60'
                  }
                  label="Flair size"
                  onChange={(e) => {
                    const v = e.target.value as string;
                    const w = 60;
                    const h = v === '60x40' ? 40 : 60;
                    setNewItemData({ ...newItemData, flair_width: w, flair_height: h });
                  }}
                >
                  <MenuItem value="60x60">60×60 (square)</MenuItem>
                  <MenuItem value="60x40">60×40 (wide)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Class Initial"
                value={newItemData.class_initial || ''}
                onChange={(e) => setNewItemData({ ...newItemData, class_initial: e.target.value })}
              />
            </>
          )}
          {tableName === 'family' && (
            <>
              <FormControl fullWidth>
                <InputLabel shrink={true} sx={{ backgroundColor: 'background.paper', px: 1 }}>
                  Class
                </InputLabel>
                <Select
                  native
                  value={newItemData.class_name || ''}
                  onChange={(e) => setNewItemData({ ...newItemData, class_name: e.target.value })}
                  label="Class"
                  sx={{
                    '& select': {
                      paddingTop: '16.5px',
                      paddingBottom: '16.5px',
                    }
                  }}
                >
                  <option value="" disabled style={{ display: 'none' }}></option>
                    {(parentOptions.class || []).map((r: any) => (
                    <option key={r.class_id} value={r.class_name}>{r.class_name}</option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Family Name"
                value={newItemData.family_name || ''}
                onChange={(e) => setNewItemData({ ...newItemData, family_name: e.target.value })}
                required
              />
              <TextField
                label="Order"
                type="number"
                value={newItemData.family_order ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, family_order: parseInt(e.target.value) || 0 })}
                helperText="0 = alphabetical order, >0 = custom numeric order"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newItemData.selectable_flair === 1 || newItemData.selectable_flair === true}
                    onChange={(e) => setNewItemData({ ...newItemData, selectable_flair: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Selectable Flair"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newItemData.selectable_inline === 1 || newItemData.selectable_inline === true}
                    onChange={(e) => setNewItemData({ ...newItemData, selectable_inline: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Selectable Inline"
              />
            </>
          )}
          {tableName === 'genus' && (
            <>
              <FormControl fullWidth>
                <InputLabel shrink={true} sx={{ backgroundColor: 'background.paper', px: 1 }}>
                  Family
                </InputLabel>
                <Select
                  native
                  value={newItemData.family_name || ''}
                  onChange={(e) => setNewItemData({ ...newItemData, family_name: e.target.value })}
                  label="Family"
                  sx={{
                    '& select': {
                      paddingTop: '16.5px',
                      paddingBottom: '16.5px',
                    }
                  }}
                >
                  <option value="" disabled style={{ display: 'none' }}></option>
                  {(parentOptions.family || []).map((r: any) => (
                    <option key={r.family_id} value={r.family_name}>{r.family_name}</option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Genus Name"
                value={newItemData.genus_name || ''}
                onChange={(e) => setNewItemData({ ...newItemData, genus_name: e.target.value })}
                required
              />
              <TextField
                label="Order"
                type="number"
                value={newItemData.genus_order ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, genus_order: parseInt(e.target.value) || 0 })}
                helperText="0 = alphabetical order, >0 = custom numeric order"
              />
              <TextField
                label="Genus Image (Flair Code)"
                value={newItemData.genus_image || ''}
                onChange={(e) => setNewItemData({ ...newItemData, genus_image: e.target.value })}
                helperText="Enter the flair_code to use as the genus image"
              />
            </>
          )}
          {tableName === 'flair' && (
            <>
              <FormControl fullWidth>
                <InputLabel shrink={true} sx={{ backgroundColor: 'background.paper', px: 1 }}>
                  Genus
                </InputLabel>
                <Select
                  native
                  value={newItemData.genus_name || ''}
                  onChange={(e) => setNewItemData({ ...newItemData, genus_name: e.target.value })}
                  label="Genus"
                  sx={{
                    '& select': {
                      paddingTop: '16.5px',
                      paddingBottom: '16.5px',
                    }
                  }}
                >
                  <option value="" disabled style={{ display: 'none' }}></option>
                  {(parentOptions.genus || []).map((r: any) => (
                    <option key={r.genus_id} value={r.genus_name}>{r.genus_name}</option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Flair Name"
                value={newItemData.flair_name || ''}
                onChange={(e) => setNewItemData({ ...newItemData, flair_name: e.target.value })}
                required
              />
              <TextField
                label="Order"
                type="number"
                value={newItemData.flair_order ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, flair_order: parseInt(e.target.value) || 0 })}
                helperText="0 = alphabetical order, >0 = custom numeric order"
              />
              <TextField
                label="Flair Text"
                value={newItemData.flair_text || ''}
                onChange={(e) => setNewItemData({ ...newItemData, flair_text: e.target.value })}
                multiline
              />
              <TextField
                label="Flair Abbreviated"
                value={newItemData.flair_abbreviated || ''}
                onChange={(e) => setNewItemData({ ...newItemData, flair_abbreviated: e.target.value })}
              />
              <TextField
                label="Is Award"
                type="number"
                value={newItemData.is_award ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, is_award: parseInt(e.target.value) })}
              />
            </>
          )}
          {tableName === 'image' && (
            <>
              <FormControl fullWidth>
                <InputLabel shrink={true} sx={{ backgroundColor: 'background.paper', px: 1 }}>
                  Flair
                </InputLabel>
                <Select
                  native
                  value={newItemData.flair_name || ''}
                  onChange={(e) => setNewItemData({ ...newItemData, flair_name: e.target.value })}
                  label="Flair"
                  sx={{
                    '& select': {
                      paddingTop: '16.5px',
                      paddingBottom: '16.5px',
                    }
                  }}
                >
                  <option value="" disabled style={{ display: 'none' }}></option>
                  {(parentOptions.flair || []).map((r: any) => (
                    <option key={r.flair_id} value={r.flair_name}>{r.flair_name}</option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Image Name"
                value={newItemData.image_name || ''}
                onChange={(e) => setNewItemData({ ...newItemData, image_name: e.target.value })}
                required
              />
              <TextField
                label="Image Description"
                value={newItemData.image_description ?? ''}
                onChange={(e) => setNewItemData({ ...newItemData, image_description: e.target.value })}
                helperText="Unique label for image view (e.g. 'Duke Blue Devils faded'). Defaults to Image Name."
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const stem = file.name.replace(/\.[^/.]+$/, '');
                    setNewItemData({
                      ...newItemData,
                      imageFile: file,
                      image_name: stem,
                      image_source: stem,
                    });
                  }
                }}
                style={{ marginTop: '8px' }}
              />
              <TextField
                label="Order"
                type="number"
                value={newItemData.image_order ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, image_order: parseInt(e.target.value) || 0 })}
                helperText="0 = alphabetical order, >0 = custom numeric order"
              />
              <TextField
                label="Flair Code"
                value={newItemData.flair_code || ''}
                onChange={(e) => setNewItemData({ ...newItemData, flair_code: e.target.value })}
              />
              <TextField
                label="Shortcut Flair"
                value={newItemData.shortcut_flair || ''}
                onChange={(e) => setNewItemData({ ...newItemData, shortcut_flair: e.target.value })}
              />
              <TextField
                label="Shortcut Inline"
                value={newItemData.shortcut_inline || ''}
                onChange={(e) => setNewItemData({ ...newItemData, shortcut_inline: e.target.value })}
                helperText="Comma-separated shortcuts (e.g. #f/mlb,/MLB,/r/sub#flair,team-x). Each becomes a CSS rule."
              />
              <TextField
                label="Default Flair"
                type="number"
                value={newItemData.default_flair ?? 0}
                onChange={(e) => setNewItemData({ ...newItemData, default_flair: parseInt(e.target.value) })}
              />
            </>
          )}
          {!isSubscribed && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAddItem(tableName)}
              disabled={
                isAddingItem ||
                (tableName === 'class' && !newItemData.class_name) ||
                (tableName === 'family' && (!newItemData.family_name || !newItemData.class_name)) ||
                (tableName === 'genus' && (!newItemData.genus_name || !newItemData.family_name)) ||
                (tableName === 'flair' && (!newItemData.flair_name || !newItemData.genus_name)) ||
                (tableName === 'image' && (!newItemData.image_name || !newItemData.flair_name))
              }
            >
              {isAddingItem ? 'Adding...' : `Add ${tableName.charAt(0).toUpperCase() + tableName.slice(1)}`}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
